/**
 * Popup carrito abandonado — Apodemia (portado de susmies)
 * Shows a recovery popup when the user returns to the site after being
 * inactive for N minutes and still has items in cart. Reuses cart upsell
 * markup, so add-to-cart works through the existing delegated handler in
 * iurop-cart-drawer.js (the [data-upsell-add] global click handler).
 *
 * Triggers:
 *   - On page load: if (now - last_active) > trigger_minutes AND cart.has_items
 *     AND popup wasn't snoozed.
 *   - On cart:updated: re-render via Section Rendering API to keep first item
 *     in sync (mirrors cart-drawer pattern).
 */
(function () {
  'use strict';

  const SECTION_ID = 'iurop-abandoned-cart';
  const LAST_ACTIVE_KEY = 'iurop:ac:last-active';
  const SNOOZE_KEY = 'iurop:ac:snooze-until';
  const SESSION_SHOWN_KEY = 'iurop:ac:session-shown';

  const qs = (sel, root) => (root || document).querySelector(sel);
  const qsa = (sel, root) => Array.from((root || document).querySelectorAll(sel));

  const now = () => Date.now();

  const getDrawer = () => qs('[data-abandoned-cart]');

  const isEnabled = (drawer) => drawer && drawer.dataset.enabled === 'true';
  const hasItem = (drawer) => drawer && drawer.dataset.hasItem === 'true';

  const triggerMs = (drawer) => {
    const min = parseInt(drawer.dataset.triggerMinutes, 10) || 30;
    return min * 60 * 1000;
  };
  const snoozeMs = (drawer) => {
    const hrs = parseInt(drawer.dataset.snoozeHours, 10) || 24;
    return hrs * 60 * 60 * 1000;
  };

  // ------------------------------------------------------------------
  // Open / close
  // ------------------------------------------------------------------
  const openPopup = () => {
    const drawer = getDrawer();
    if (!drawer || !hasItem(drawer)) return;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    if (window.matchMedia('(max-width: 749px)').matches) {
      document.body.classList.add('apodemia-abandoned-open');
    }
    try { sessionStorage.setItem(SESSION_SHOWN_KEY, '1'); } catch (_) {}
  };

  const closePopup = () => {
    const drawer = getDrawer();
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('apodemia-abandoned-open');
    try {
      const until = now() + snoozeMs(drawer);
      localStorage.setItem(SNOOZE_KEY, String(until));
    } catch (_) {}
  };

  // ------------------------------------------------------------------
  // Activity tracking — write last-active to localStorage
  // ------------------------------------------------------------------
  const writeLastActive = () => {
    try { localStorage.setItem(LAST_ACTIVE_KEY, String(now())); } catch (_) {}
  };

  const startActivityTracking = () => {
    // Heartbeat every 60s while the tab is visible. The first heartbeat is
    // delayed so the page-load `shouldShow` check can read the *previous*
    // last-active value before we overwrite it.
    let heartbeat = null;
    const start = () => {
      if (heartbeat) return;
      heartbeat = setInterval(writeLastActive, 60 * 1000);
    };
    const stop = () => {
      if (!heartbeat) return;
      writeLastActive();
      clearInterval(heartbeat);
      heartbeat = null;
    };
    if (!document.hidden) start();
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stop(); else start();
    });
    window.addEventListener('beforeunload', writeLastActive);
    window.addEventListener('pagehide', writeLastActive);
  };

  // ------------------------------------------------------------------
  // Should-show decision
  // ------------------------------------------------------------------
  const shouldShow = (drawer) => {
    if (!isEnabled(drawer) || !hasItem(drawer)) return false;

    try {
      if (sessionStorage.getItem(SESSION_SHOWN_KEY) === '1') return false;
    } catch (_) {}

    try {
      const snoozeUntil = parseInt(localStorage.getItem(SNOOZE_KEY), 10);
      if (snoozeUntil && snoozeUntil > now()) return false;
    } catch (_) {}

    let lastActive = 0;
    try { lastActive = parseInt(localStorage.getItem(LAST_ACTIVE_KEY), 10) || 0; } catch (_) {}

    // No record yet → first visit, don't show. Just start tracking.
    if (!lastActive) return false;

    const elapsed = now() - lastActive;
    return elapsed >= triggerMs(drawer);
  };

  // ------------------------------------------------------------------
  // Re-render from server (keeps first item / upsells in sync)
  // ------------------------------------------------------------------
  const refresh = () => {
    return fetch(`/?sections=${SECTION_ID}`, { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((data) => {
        const html = data[SECTION_ID];
        if (!html) return;
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newDrawer = doc.querySelector('[data-abandoned-cart]');
        const currentDrawer = getDrawer();
        if (newDrawer && currentDrawer) {
          const wasOpen = currentDrawer.classList.contains('is-open');
          currentDrawer.dataset.hasItem = newDrawer.dataset.hasItem;
          currentDrawer.innerHTML = newDrawer.innerHTML;
          if (wasOpen && hasItem(currentDrawer)) {
            currentDrawer.classList.add('is-open');
          } else if (!hasItem(currentDrawer)) {
            currentDrawer.classList.remove('is-open');
            currentDrawer.setAttribute('aria-hidden', 'true');
            document.body.classList.remove('apodemia-abandoned-open');
          }
          bindEvents();
        }
      })
      .catch((err) => console.error('[abandoned-cart] refresh failed', err));
  };

  // ------------------------------------------------------------------
  // Upsell carousel — drag + progress sync (lifted from cart drawer)
  // ------------------------------------------------------------------
  const initUpsellCarousel = () => {
    const track = qs('[data-abandoned-upsells-track]');
    if (!track) return;

    const segments = qsa('.apodemia-abandoned__upsells-progress-segment');
    const cards = qsa('[data-upsell-row]', track);

    let isDown = false;
    let startX = 0;
    let startScrollLeft = 0;
    let moved = false;

    track.addEventListener('pointerdown', (e) => {
      if (e.button !== undefined && e.button !== 0) return;
      isDown = true;
      moved = false;
      startX = e.clientX;
      startScrollLeft = track.scrollLeft;
    });
    track.addEventListener('pointermove', (e) => {
      if (!isDown) return;
      const dx = e.clientX - startX;
      if (!moved && Math.abs(dx) > 4) {
        moved = true;
        track.classList.add('is-dragging');
        try { track.setPointerCapture(e.pointerId); } catch (_) {}
      }
      if (moved) track.scrollLeft = startScrollLeft - dx;
    });
    const endDrag = (e) => {
      if (!isDown) return;
      isDown = false;
      track.classList.remove('is-dragging');
      if (moved) {
        try { track.releasePointerCapture(e.pointerId); } catch (_) {}
      }
    };
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener('pointerleave', endDrag);
    track.addEventListener('click', (e) => {
      if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; }
    }, true);

    if (segments.length && cards.length && 'IntersectionObserver' in window) {
      const setActive = (idx) => {
        segments.forEach((s, i) => s.classList.toggle('is-active', i === idx));
      };
      const observer = new IntersectionObserver(
        (entries) => {
          let best = null;
          entries.forEach((entry) => {
            if (!best || entry.intersectionRatio > best.intersectionRatio) best = entry;
          });
          if (best && best.isIntersecting) {
            const idx = cards.indexOf(best.target);
            if (idx >= 0) setActive(idx);
          }
        },
        { root: track, threshold: [0.5, 0.75, 1] }
      );
      cards.forEach((c) => observer.observe(c));
    }
  };

  // ------------------------------------------------------------------
  // Bind close handlers
  // ------------------------------------------------------------------
  const bindEvents = () => {
    qsa('[data-abandoned-cart-close]').forEach((el) => {
      el.addEventListener('click', closePopup);
    });
    initUpsellCarousel();
  };

  // ------------------------------------------------------------------
  // Añadir upsell — delegado y SCOPED al popup (el futuro sidecart tendrá el
  // suyo). POST /cart/add.js → cart:updated con noOpen (el drawer refresca en
  // silencio y este popup se re-renderiza vía su propio listener; la fila
  // añadida desaparece del carrusel porque el filtro excluye lo que ya está
  // en el carrito — ese es el feedback).
  // ------------------------------------------------------------------
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-upsell-add]');
    if (!btn || !btn.closest('[data-abandoned-cart]') || btn.disabled) return;
    e.preventDefault();
    btn.disabled = true;
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(btn.dataset.variantId, 10), quantity: 1 }] }),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(() => fetch('/cart.js').then((r) => r.json()))
      .then((cart) => {
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:updated', { bubbles: true, detail: { cart, noOpen: true } })
        );
      })
      .catch(() => { btn.disabled = false; });
  });

  // ------------------------------------------------------------------
  // Init
  // ------------------------------------------------------------------
  const init = () => {
    const drawer = getDrawer();
    if (!drawer) return;

    bindEvents();

    // Decide BEFORE starting tracking — otherwise the heartbeat would
    // overwrite the previous last-active and elapsed would always be 0.
    const willShow = shouldShow(drawer);

    startActivityTracking();

    if (willShow) {
      // Slight delay so it doesn't pop the instant the page paints
      setTimeout(openPopup, 800);
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        const d = getDrawer();
        if (d && d.classList.contains('is-open')) closePopup();
      }
    });

    // Stay in sync with cart changes (qty/remove/upsell-add). The cart
    // drawer dispatches cart:updated; we refresh quietly without opening.
    document.addEventListener('cart:updated', () => {
      refresh();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (e) => {
    if (e.target.querySelector('[data-abandoned-cart]')) {
      bindEvents();
    }
  });

  // Public testing API — call window.iuropAbandonedCart.show() to force the
  // popup, .close() to dismiss, .reset() to clear all snooze/session flags.
  window.iuropAbandonedCart = {
    show: openPopup,
    close: closePopup,
    reset: () => {
      try {
        localStorage.removeItem(LAST_ACTIVE_KEY);
        localStorage.removeItem(SNOOZE_KEY);
        sessionStorage.removeItem(SESSION_SHOWN_KEY);
      } catch (_) {}
    },
    debug: () => {
      const drawer = getDrawer();
      let last = null, snooze = null, sess = null;
      try { last = parseInt(localStorage.getItem(LAST_ACTIVE_KEY), 10) || null; } catch (_) {}
      try { snooze = parseInt(localStorage.getItem(SNOOZE_KEY), 10) || null; } catch (_) {}
      try { sess = sessionStorage.getItem(SESSION_SHOWN_KEY); } catch (_) {}
      return {
        enabled: isEnabled(drawer),
        hasItem: hasItem(drawer),
        triggerMinutes: drawer && parseInt(drawer.dataset.triggerMinutes, 10),
        snoozeHours: drawer && parseInt(drawer.dataset.snoozeHours, 10),
        lastActive: last && new Date(last).toISOString(),
        elapsedMinutes: last ? Math.round((Date.now() - last) / 60000) : null,
        snoozeUntil: snooze && new Date(snooze).toISOString(),
        sessionShown: sess === '1',
        wouldShowNow: shouldShow(drawer),
      };
    },
  };
})();
